<!-- 
Estimator panel.

Shows the current status of the public levy estimator and the currently available actions
(publish, unpublish, enable, disable, preview).
-->
<template>
  <v-card class="flat mb-4">
    <v-card-title class="mb-0 pb-1">
      <h3 class="title">
        Public levy estimator
        <v-chip v-if="!enabled" small color="grey" text-color="white">Disabled</v-chip>
        <v-chip v-else-if="finalised" small color="grey" text-color="white">Unavailable</v-chip>
        <v-chip v-else-if="published" small color="green" text-color="white">Published</v-chip>
        <v-chip v-else small color="orange" text-color="white">Not published</v-chip>
        <our-user-guide-button topic="levy-collection-setup" section="public-levy-estimator" in-title/>
      </h3>
    </v-card-title>
    <v-card-text v-if="!enabled">
      The public levy estimator is not included in this levy collection. Please ensure any links to it are removed from the notice templates and any other communications.
    </v-card-text>
    <v-card-text v-else-if="finalised">
      The public levy estimator for this levy collection is no longer visible to the public because the levy collection is finalised. 
    </v-card-text>
    <v-card-text v-else-if="published">
      The public levy estimator for this levy collection has been published and is visible to the public. 
      You would normally supply providers with a link to the public levy estimator in the
      <router-link :to="earlyAdviceNotice">Early advice notice</router-link>
      using the <i>LevyEstimatorLink</i> tag.
    </v-card-text>
    <v-card-text v-else>
      The public levy estimator for this levy collection has not yet been published, so it is not yet visible to the public.
      You would normally supply providers with a link to the public levy estimator in the
      <router-link :to="earlyAdviceNotice">Early advice notice</router-link>
      using the <i>LevyEstimatorLink</i> tag.
    </v-card-text>
    <v-card-actions v-if="enabled">
      <v-btn :to="estimatorUrl" class="ml-2">
        <v-icon class="mr-2">local_atm</v-icon>
        {{ published ? "Go to levy estimator" : "Preview the levy estimator" }}
      </v-btn>
      <v-btn v-if="canPublish" @click="showPublishDialog = true">
        <v-icon class="mr-2">publish</v-icon>
        Publish public levy estimator
      </v-btn>
      <v-btn v-if="canUnpublish" @click="showUnpublishDialog = true">
        <v-icon class="mr-2">publish</v-icon>
        Un-publish public levy estimator
      </v-btn>
      <v-btn v-if="canEnable" @click="showEditDialog=true">
        Exclude the public levy estimator
      </v-btn>
      <run-task-dialog v-model="showPublishDialog" :levy-collection-id="levyCollectionId" :task="publishTask"/>
      <run-task-dialog v-model="showUnpublishDialog" :levy-collection-id="levyCollectionId" :task="unpublishTask"/>
    </v-card-actions>
    <v-card-actions v-else>
      <v-btn v-if="canEnable" class="ml-2" @click="showEditDialog=true">
        Include the public levy estimator
      </v-btn>
    </v-card-actions>
    <estimator-configuration-edit-dialog v-model="showEditDialog" :levy-collection-id="levyCollectionId" />
  </v-card>
</template>

<script>
import RunTaskDialog from "@/components/levy-collections/RunTaskDialog.vue";
import EstimatorConfigurationEditDialog from "@/components/estimator-templates/EstimatorConfigurationEditDialog.vue";
import * as action from "@/store/levy-collections/action-types";
import * as claim from "@/store/claim-types";

export default {
  components: { RunTaskDialog, EstimatorConfigurationEditDialog },
  props: {
    levyCollectionId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      showRunTaskDialog: false,
      showPublishDialog: false,
      showUnpublishDialog: false,
      showEditDialog: false
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollections.view;
    },
    levyCollection() {
      return this.state.data;
    },
    loaded() {
      return this.state.loaded;
    },
    tasks() {
      return this.$store.state.levyCollections.tasks;
    },
    tasksLoaded() {
      return this.tasks.loaded;
    },
    enabled() {
      return this.levyCollection.enablePublicLevyEstimator;
    },
    finalised() {
      return this.levyCollection.status == "Finalised";
    },
    published() {
      return this.levyCollection.publicEstimatorPublished;
    },
    estimatorUrl() {
      return `/public-levy-estimator/${this.levyCollection.type}/${this.levyCollection.year}`;
    },
    earlyAdviceNotice() {
      return `/levy-collections/${this.levyCollectionId}/setup/notices/EarlyAdviceNotice`;
    },
    publishTask() {
      const task = this.tasks.data.find(t => t.type == "PublishPublicEstimator");
      return task ? task : { type: "", description: "" };
    },
    canPublish() {
      return this.publishTask && this.publishTask.isApplicable
        ? this.$store.getters.claims.includes(this.publishTask.claimType)
        : false;
    },
    unpublishTask() {
      const task = this.tasks.data.find(t => t.type == "UnpublishPublicEstimator");
      return task ? task : { type: "", description: "" };
    },
    canUnpublish() {
      return this.unpublishTask && this.unpublishTask.isApplicable
        ? this.$store.getters.claims.includes(this.unpublishTask.claimType)
        : false;
    },
    canEnable() {
      return (
        this.levyCollection.status != "Finalised" &&
        this.$store.getters.claims.includes(claim.LEVY_COLLECTION_CONFIGURE_PUBLIC_LEVY_ESTIMATOR)
      );
    }
  },
  watch: {
    loaded() {
      this.refresh();
    },
    tasksLoaded() {
      this.refresh();
    },
    levyCollectionId() {
      this.refresh();
    }
  },
  mounted() {
    this.refresh();
  },
  methods: {
    refresh() {
      this.$store.dispatch(action.VIEW_REFRESH, this.levyCollectionId);
      if ((!this.tasks.loaded && !this.tasks.loading) || this.tasks.levyCollectionId != this.levyCollectionId) {
        this.$store.dispatch(action.TASKS_GET, this.levyCollectionId);
      }
    }
  }
};
</script>
